import React, { useState } from "react";
import {
  Card,
  Select,
  Table,
  Typography,
  Form,
  InputNumber,
  Button,
  Space,
  message,
  Alert,
  Divider,
  Checkbox,
  Tag,
} from "antd";
import { Rule, Component } from "../../types/pricing";
import { supabase } from "../../lib/supabase";
import { trackEvent, captureError } from "../../lib/posthog";

const { Text } = Typography;
const { Option } = Select;

interface RuleValuesTabProps {
  rules: Rule[];
}

interface ComponentWithValue extends Component {
  value: number | null;
  unit: string | null;
  currency?: string;
  included_products?: number;
  extra_product_cost?: number;
  isIncluded?: boolean;
}

interface RuleValueFormValues {
  [key: string]: number | string | null;
}

interface ValidationResult {
  hasIssues: boolean;
  issues: {
    ruleId: string;
    ruleName: string;
    componentId: string;
    componentName: string;
    issue: string;
  }[];
}

interface PricingValue {
  id?: string;
  pricing_rule_id: string;
  pricing_component_id: string;
  value: number;
  unit: string | null;
  included_products: number | null;
  currency: string | null;
  extra_product_cost: number | null;
}

const RuleValuesTab: React.FC<RuleValuesTabProps> = ({ rules }) => {
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [components, setComponents] = useState<ComponentWithValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [form] = Form.useForm();
  const [selectedComponents, setSelectedComponents] = useState<Set<string>>(
    new Set()
  );

  const fetchComponentsForRule = async (ruleId: string) => {
    try {
      setLoading(true);
      console.log("Fetching components for rule:", ruleId);

      const { data: allComponents, error: componentsError } = await supabase
        .from("pricing_components")
        .select("*")
        .order("name");

      console.log("Fetched components:", allComponents);

      if (componentsError) throw componentsError;

      const { data: existingValues, error: valuesError } = await supabase
        .from("pricing_values")
        .select("*")
        .eq("pricing_rule_id", ruleId);

      console.log("Fetched existing values:", existingValues);

      if (valuesError) throw valuesError;

      // Mark components that already have values as included
      const includedComponentIds = new Set(
        existingValues?.map((v) => v.pricing_component_id) || []
      );
      console.log("Included component IDs:", Array.from(includedComponentIds));
      setSelectedComponents(includedComponentIds);

      const componentsWithValues = allComponents?.map((component) => {
        const existingValue = existingValues?.find(
          (v) => v.pricing_component_id === component.id
        );
        console.log(
          "Component:",
          component.id,
          "Existing value:",
          existingValue
        );
        return {
          ...component,
          value: existingValue?.value || null,
          unit: existingValue?.unit || getComponentUnit(component),
          included_products: existingValue?.included_products || null,
          extra_product_cost: existingValue?.extra_product_cost || null,
          isIncluded: includedComponentIds.has(component.id),
          currency: existingValue?.currency || "EUR",
        };
      });

      console.log("Components with values:", componentsWithValues);
      setComponents(componentsWithValues || []);

      // Create form field values for all components
      const formValues: RuleValueFormValues = {};
      componentsWithValues?.forEach((component) => {
        if (component.isIncluded) {
          // Set value field
          formValues[`value-${component.id}`] = component.value;

          // Set currency field if not percentage
          if (component.calculation_type !== "percentage") {
            formValues[`currency-${component.id}`] =
              component.currency || "EUR";
          }

          // Set included_products and extra_product_cost for per_product components
          if (
            component.calculation_type === "per_product" ||
            component.calculation_type === "per_unique_product"
          ) {
            formValues[`included_products-${component.id}`] =
              component.included_products;
            formValues[`extra_product_cost-${component.id}`] =
              component.extra_product_cost;
          }
        }
      });

      // Reset form and set values
      form.resetFields();
      form.setFieldsValue(formValues);
    } catch (error) {
      console.error("Error fetching components:", error);
      message.error("Failed to fetch components");
      captureError(error, {
        source: "RuleValuesTab",
        action: "fetchComponentsForRule",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRuleChange = (ruleId: string) => {
    setSelectedRuleId(ruleId);
    // We'll handle form reset in fetchComponentsForRule
    fetchComponentsForRule(ruleId);
    trackEvent("pricing_rule_selected", { rule_id: ruleId });
  };

  const getComponentUnit = (component: Component): string | null => {
    if (!component.calculation_type) return null;

    switch (component.calculation_type) {
      case "fixed":
        return "fixed";
      case "percentage":
        return "percentage";
      case "per_kg":
        return "per_kg";
      case "per_cbm":
        return "per_cbm";
      case "per_container":
        return "per_container";
      case "per_product":
        return "per_product";
      case "per_unique_product":
        return "per_unique_product";
      case "per_carton":
        return "per_carton";
      case "per_pallet":
        return "per_pallet";
      case "per_shipment":
        return "per_shipment";
      default:
        return null;
    }
  };

  const handleSubmit = async (values: RuleValueFormValues) => {
    try {
      setSaving(true);

      // Get all existing values for this rule
      const { data: existingValues, error: fetchError } = await supabase
        .from("pricing_values")
        .select("*")
        .eq("pricing_rule_id", selectedRuleId);

      if (fetchError) throw fetchError;

      // Delete values for components that are no longer included
      const valuesToDelete = existingValues?.filter(
        (value) => !selectedComponents.has(value.pricing_component_id)
      );

      if (valuesToDelete?.length) {
        const { error: deleteError } = await supabase
          .from("pricing_values")
          .delete()
          .in(
            "id",
            valuesToDelete.map((v) => v.id)
          );

        if (deleteError) throw deleteError;
      }

      // Create a map of existing values for lookup
      const existingValuesMap = new Map(
        existingValues?.map((v) => [v.pricing_component_id, v]) || []
      );

      // Prepare updates and inserts
      const updates: PricingValue[] = [];
      const inserts: Omit<PricingValue, "id">[] = [];

      // Process each selected component
      selectedComponents.forEach((componentId) => {
        const component = components.find((c) => c.id === componentId);
        if (!component) return;

        const value = Number(values[`value-${componentId}`] || 0);
        const currency = String(values[`currency-${componentId}`] || "EUR");
        const included_products =
          component.calculation_type === "per_product" ||
          component.calculation_type === "per_unique_product"
            ? Number(values[`included_products-${componentId}`] || 0)
            : null;
        const extra_product_cost =
          component.calculation_type === "per_product" ||
          component.calculation_type === "per_unique_product"
            ? Number(values[`extra_product_cost-${componentId}`] || 0)
            : null;

        const pricingValue = {
          pricing_rule_id: selectedRuleId!,
          pricing_component_id: componentId,
          value,
          unit: component.calculation_type || null,
          currency:
            component.calculation_type === "percentage" ? "EUR" : currency, // Default to EUR for percentage types
          included_products,
          extra_product_cost,
        };

        if (existingValuesMap.has(componentId)) {
          updates.push({
            id: existingValuesMap.get(componentId)!.id,
            ...pricingValue,
          });
        } else {
          inserts.push(pricingValue);
        }
      });

      // Perform updates
      if (updates.length > 0) {
        const { error: updateError } = await supabase
          .from("pricing_values")
          .upsert(updates);

        if (updateError) throw updateError;
      }

      // Perform inserts
      if (inserts.length > 0) {
        const { error: insertError } = await supabase
          .from("pricing_values")
          .insert(inserts);

        if (insertError) throw insertError;
      }

      message.success("Values saved successfully");
      fetchComponentsForRule(selectedRuleId!);
    } catch (error) {
      console.error("Error saving values:", error);
      message.error("Failed to save values");
      captureError(error, {
        source: "RuleValuesTab",
        action: "handleSubmit",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleComponentToggle = (componentId: string, checked: boolean) => {
    const newSelectedComponents = new Set(selectedComponents);
    if (checked) {
      newSelectedComponents.add(componentId);
    } else {
      newSelectedComponents.delete(componentId);
    }
    setSelectedComponents(newSelectedComponents);

    // Update the components list to reflect the change
    setComponents(
      components.map((component) => ({
        ...component,
        isIncluded: newSelectedComponents.has(component.id),
      }))
    );
  };

  const validateAllRules = async () => {
    try {
      setLoading(true);
      const issues: ValidationResult["issues"] = [];

      // Fetch all components first
      const { data: allComponents, error: componentsError } = await supabase
        .from("pricing_components")
        .select("*")
        .order("name");

      console.log("All components:", allComponents);

      if (componentsError) throw componentsError;

      // Fetch all pricing values
      const { data: allValues, error: valuesError } = await supabase
        .from("pricing_values")
        .select("*");

      console.log("All pricing values:", allValues);

      if (valuesError) throw valuesError;

      console.log("Rules to check:", rules);

      // Check each rule
      for (const rule of rules) {
        console.log("Checking rule:", rule);
        if (!rule.is_active) {
          console.log("Rule is not active, skipping:", rule.id);
          continue;
        }

        const ruleValues = allValues.filter(
          (v) => v.pricing_rule_id === rule.id
        );
        console.log("Values for rule:", rule.id, ruleValues);

        const includedComponentIds = new Set(
          ruleValues.map((v) => v.pricing_component_id)
        );
        console.log(
          "Included component IDs:",
          Array.from(includedComponentIds)
        );

        // Only check components that are included for this rule
        for (const component of allComponents) {
          if (!includedComponentIds.has(component.id)) {
            console.log("Component not included for rule:", component.id);
            continue;
          }

          const value = ruleValues.find(
            (v) => v.pricing_component_id === component.id
          );
          console.log("Found value for component:", component.id, value);

          if (!value) {
            issues.push({
              ruleId: rule.id,
              ruleName: rule.name,
              componentId: component.id,
              componentName: component.name,
              issue: "Missing value",
            });
            continue;
          }

          // Check if value is 0 or null
          if (value.value === 0 || value.value === null) {
            issues.push({
              ruleId: rule.id,
              ruleName: rule.name,
              componentId: component.id,
              componentName: component.name,
              issue: "Value is 0 or null",
            });
          }

          // Check if unit matches calculation type
          const expectedUnit = getComponentUnit(component);
          if (value.unit !== expectedUnit) {
            issues.push({
              ruleId: rule.id,
              ruleName: rule.name,
              componentId: component.id,
              componentName: component.name,
              issue: `Incorrect unit: expected ${expectedUnit}, got ${value.unit}`,
            });
          }

          // For per_container components, check included_products
          if (
            component.calculation_type === "per_container" &&
            !value.included_products
          ) {
            issues.push({
              ruleId: rule.id,
              ruleName: rule.name,
              componentId: component.id,
              componentName: component.name,
              issue:
                "Missing included products count for per_container component",
            });
          }
        }
      }

      console.log("Validation issues found:", issues);

      setValidationResult({
        hasIssues: issues.length > 0,
        issues,
      });

      if (issues.length > 0) {
        message.warning(`Found ${issues.length} issues with pricing values`);
      } else {
        message.success("All pricing values are properly set");
      }
    } catch (error) {
      console.error("Error validating rules:", error);
      message.error("Failed to validate pricing values");
      captureError(error, {
        source: "RuleValuesTab",
        action: "validateAllRules",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return "No end date";
    return new Date(date).toLocaleDateString();
  };

  const getRuleLabel = (rule: Rule) => {
    const parts = [];

    // Add origin and destination if not ALL
    if (rule.origin_port) {
      parts.push(rule.origin_port.name);
    } else {
      parts.push("All Origins");
    }

    if (rule.destination_port) {
      parts.push(rule.destination_port.name);
    } else {
      parts.push("All Destinations");
    }

    // Add transport type and shipment type
    if (rule.transport_type) {
      // Convert transport_type to proper display format
      const transportType =
        typeof rule.transport_type === "object" && rule.transport_type !== null
          ? rule.transport_type.name || "Unknown Transport"
          : rule.transport_type;
      parts.push(transportType);
    } else {
      parts.push("All Transport Types");
    }

    if (rule.shipment_type) {
      parts.push(rule.shipment_type.name);
    } else {
      parts.push("All Shipments");
    }

    // Add incoterm if present
    if (rule.incoterm) {
      parts.push(rule.incoterm.name);
    } else {
      parts.push("All Incoterms");
    }

    // Add container size if present
    if (rule.container_size) {
      parts.push(rule.container_size.name);
    }

    return parts.join(" • ");
  };

  const columns = [
    {
      title: "Include",
      dataIndex: "isIncluded",
      key: "include",
      width: 80,
      render: (_: unknown, record: ComponentWithValue) => (
        <Checkbox
          checked={selectedComponents.has(record.id)}
          onChange={(e) => handleComponentToggle(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: "Component",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text: string, record: Component) => (
        <span>
          {text}
          {record.description && (
            <Text
              type="secondary"
              style={{ display: "block", fontSize: "12px" }}
            >
              {record.description}
            </Text>
          )}
        </span>
      ),
    },
    {
      title: "Value",
      key: "value",
      width: 150,
      render: (_: unknown, record: ComponentWithValue) => (
        <Form.Item
          name={`value-${record.id}`}
          rules={[
            {
              required: selectedComponents.has(record.id),
              message: "Please enter value",
            },
          ]}
          initialValue={components.find((c) => c.id === record.id)?.value}
        >
          <InputNumber
            style={{ width: "100%" }}
            precision={2}
            placeholder="Enter value"
            addonAfter={record.calculation_type === "percentage" ? "%" : null}
            disabled={!selectedComponents.has(record.id)}
          />
        </Form.Item>
      ),
    },
    {
      title: "Unit",
      key: "unit",
      width: 150,
      render: (_: unknown, record: ComponentWithValue) => {
        const unit = record.calculation_type;
        return (
          <span
            style={{
              color: selectedComponents.has(record.id)
                ? "inherit"
                : "rgba(0, 0, 0, 0.25)",
            }}
          >
            {unit === "percentage" && "%"}
            {unit === "per_kg" && "Per KG"}
            {unit === "per_cbm" && "Per CBM"}
            {unit === "per_container" && "Per Container"}
            {unit === "fixed" && "Fixed Amount"}
            {unit === "per_product" && "Per Product"}
            {unit === "per_unique_product" && "Per Unique Product (HS Code)"}
            {unit === "per_carton" && "Per Carton"}
            {unit === "per_pallet" && "Per Pallet"}
            {unit === "per_shipment" && "Per Shipment"}
          </span>
        );
      },
    },
    {
      title: "Currency",
      key: "currency",
      width: 120,
      render: (_: unknown, record: ComponentWithValue) => (
        <Form.Item
          name={`currency-${record.id}`}
          initialValue={
            components.find((c) => c.id === record.id)?.currency || "EUR"
          }
        >
          <Select
            style={{ width: "100%" }}
            disabled={
              record.calculation_type === "percentage" ||
              !selectedComponents.has(record.id)
            }
          >
            <Option value="USD">USD</Option>
            <Option value="EUR">EUR</Option>
            <Option value="GBP">GBP</Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      title: "Included Products",
      key: "included_products",
      width: 150,
      render: (_: unknown, record: ComponentWithValue) => {
        if (
          record.calculation_type !== "per_product" &&
          record.calculation_type !== "per_unique_product"
        ) {
          return null;
        }
        return (
          <Form.Item
            name={`included_products-${record.id}`}
            initialValue={
              components.find((c) => c.id === record.id)?.included_products
            }
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              placeholder="Included products"
              disabled={!selectedComponents.has(record.id)}
            />
          </Form.Item>
        );
      },
    },
    {
      title: "Extra Product Cost",
      key: "extra_product_cost",
      width: 150,
      render: (_: unknown, record: ComponentWithValue) => {
        if (
          record.calculation_type !== "per_product" &&
          record.calculation_type !== "per_unique_product"
        ) {
          return null;
        }
        return (
          <Form.Item
            name={`extra_product_cost-${record.id}`}
            initialValue={
              components.find((c) => c.id === record.id)?.extra_product_cost
            }
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              precision={2}
              placeholder="Cost per extra product"
              disabled={!selectedComponents.has(record.id)}
            />
          </Form.Item>
        );
      },
    },
  ];

  return (
    <Card title="Manage Pricing Values">
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <div>
          <Text>Select a pricing rule to manage its component values:</Text>
          <Select
            style={{ width: "100%", marginTop: 16 }}
            placeholder="Select a pricing rule"
            onChange={handleRuleChange}
            value={selectedRuleId}
            optionLabelProp="label"
          >
            {rules.map((rule: Rule) => (
              <Option key={rule.id} value={rule.id} label={rule.name}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <div style={{ fontWeight: "bold" }}>{rule.name}</div>
                  <div
                    style={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.45)" }}
                  >
                    {getRuleLabel(rule)}
                  </div>
                  <div style={{ fontSize: "12px" }}>
                    <Tag color="blue">
                      Valid: {formatDate(rule.valid_from)} -{" "}
                      {formatDate(rule.valid_to)}
                    </Tag>
                    <Tag color={rule.is_active ? "green" : "red"}>
                      {rule.is_active ? "Active" : "Inactive"}
                    </Tag>
                    {rule.priority && (
                      <Tag color="orange">Priority: {rule.priority}</Tag>
                    )}
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </div>

        <Divider />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text strong>Validation</Text>
          <Button
            onClick={validateAllRules}
            loading={loading}
            type="primary"
            style={{ marginBottom: 16 }}
          >
            Validate All Pricing Rules
          </Button>
        </div>

        {validationResult && validationResult.hasIssues && (
          <Alert
            type="warning"
            message="Pricing Value Issues Found"
            description={
              <div style={{ maxHeight: "200px", overflow: "auto" }}>
                {validationResult.issues.map((issue, index) => (
                  <div key={index} style={{ marginBottom: "8px" }}>
                    <strong>{issue.ruleName}</strong>: {issue.componentName} -{" "}
                    {issue.issue}
                  </div>
                ))}
              </div>
            }
          />
        )}

        {selectedRuleId && (
          <>
            <Divider />
            <Form form={form} onFinish={handleSubmit}>
              <Table
                dataSource={components}
                columns={columns}
                rowKey="id"
                pagination={false}
                loading={loading}
              />
              <div style={{ marginTop: 20, textAlign: "right" }}>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Save Values
                </Button>
              </div>
            </Form>
          </>
        )}
      </Space>
    </Card>
  );
};

export default RuleValuesTab;
